import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { 
  Award, BookOpen, BarChart2, Settings,
  ChevronRight
} from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';
import { CertificatesPage } from './CertificatesPage';
import { ModulesManagementPage } from './ModulesManagementPage';
import { DashboardPage } from './DashboardPage';
import { CitiesManagementPage } from './CitiesManagementPage';
import { cn } from '../../utils/cn';
import { registerServiceWorker } from '../../utils/notifications';

type AdminSection = 'overview' | 'certificates' | 'modules' | 'cities';

export function AdminPanel() {
  const { company } = useCompany();
  const [currentSection, setCurrentSection] = useState<AdminSection>('overview');

  useEffect(() => {
    // Register service worker for push notifications
    registerServiceWorker().catch(console.error);
  }, []);

  const navigation = [
    { 
      id: 'overview',
      name: 'Visão Geral', 
      icon: BarChart2,
      description: 'Dashboard com estatísticas e informações gerais'
    },
    { 
      id: 'certificates',
      name: 'Certificações', 
      icon: Award,
      description: 'Gerenciar certificados e aprovações'
    },
    { 
      id: 'modules',
      name: 'Módulos', 
      icon: BookOpen,
      description: 'Gerenciar módulos de treinamento'
    },
    {
      id: 'cities',
      name: 'Configurações',
      icon: Settings,
      description: 'Gerenciar cidades e outras configurações'
    }
  ];

  const renderContent = () => {
    switch (currentSection) {
      case 'overview':
        return <DashboardPage />;
      case 'certificates':
        return <CertificatesPage defaultFilter="pending" />;
      case 'modules':
        return <ModulesManagementPage />;
      case 'cities':
        return <CitiesManagementPage />;
      default:
        return <DashboardPage />;
    }
  };

  return (
    <Layout>
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Navigation Menu - Responsive */}
        <div className="w-full lg:w-64 space-y-4">
          {navigation.map((item) => (
            <button
              key={item.id}
              onClick={() => setCurrentSection(item.id as AdminSection)}
              className={cn(
                "w-full flex items-center justify-between p-4 rounded-lg",
                "transition-all hover:bg-gray-50",
                currentSection === item.id 
                  ? "bg-purple-50 text-purple-700 border-2 border-purple-200"
                  : "bg-white text-gray-700 border border-gray-200"
              )}
            >
              <div className="flex items-center gap-3">
                <item.icon className="h-5 w-5" />
                <div className="text-left">
                  <p className="font-medium">{item.name}</p>
                  <p className="text-xs text-gray-500 hidden sm:block">{item.description}</p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4" />
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {renderContent()}
        </div>
      </div>
    </Layout>
  );
}